import React from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import type { PatchingBillingCycle, PatchingServiceType } from './PatchingServicesSection';
import { formatPatchingQuarterCycleLabel } from '../../utils/patchingBillingPdfSection';
import {
  getPatchingQuarterBillingMonthLabel,
  getPatchingQuarterCycleForDate
} from '../../utils/patchingQuarterDisplay';

export interface EngagementPatchingService {
  id: string;
  patchingType: PatchingServiceType;
  billingCycle: PatchingBillingCycle;
  billingAmount: number;
}

const TYPE_LABELS: Record<PatchingServiceType, string> = {
  WINDOWS: 'Windows Patching',
  LINUX: 'Linux Patching'
};

const CYCLE_LABELS: Record<PatchingBillingCycle, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly'
};

interface PatchingServicesDetailProps {
  patchingServices?: EngagementPatchingService[];
  engagementStartDate?: string | null;
  currency?: string;
}

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

export const PatchingServicesDetail: React.FC<PatchingServicesDetailProps> = ({
  patchingServices,
  engagementStartDate,
  currency = 'USD'
}) => {
  if (!patchingServices?.length) {
    return null;
  }

  const quarterCycle =
    engagementStartDate != null
      ? getPatchingQuarterCycleForDate(engagementStartDate)
      : null;
  const quarterBillingMonthLabel =
    engagementStartDate != null
      ? getPatchingQuarterBillingMonthLabel(engagementStartDate)
      : null;

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-orange-200">
      <div className="px-4 py-5 sm:px-6 bg-orange-50 border-b border-orange-100">
        <div className="flex items-center gap-2">
          <WrenchScrewdriverIcon className="h-5 w-5 text-orange-600" aria-hidden />
          <h3 className="text-lg leading-6 font-medium text-gray-900">Patching Services</h3>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Configured patching billing. Resource is billed from logged hours × agent rate; the patching
          amount below is reference only.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Service
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Billing cycle
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Patching amount
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Bill timing
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {patchingServices.map((service) => (
              <tr key={service.id}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {TYPE_LABELS[service.patchingType]}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {CYCLE_LABELS[service.billingCycle]}
                </td>
                <td className="px-4 py-3 text-sm text-orange-700 text-right font-medium">
                  {formatMoney(service.billingAmount, currency)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {service.billingCycle === 'QUARTERLY' ? (
                    <span>
                      {quarterCycle && (
                        <>
                          Quarter{' '}
                          {formatPatchingQuarterCycleLabel(
                            quarterCycle.cycleStartMonth,
                            quarterCycle.cycleEndMonth
                          )}
                          {' · '}
                        </>
                      )}
                      Bill in{' '}
                      <span className="font-medium text-sky-800">
                        {quarterBillingMonthLabel ?? '—'}
                      </span>
                    </span>
                  ) : (
                    'Each month after engagement start'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PatchingServicesDetail;
